import React from 'react';
import { Link } from 'react-router-dom';
import './AboutPage.css';
import Footer from './Footer';
import SEO from './SEO';
import ContactHome from './ContactHome';

const AboutPage = () => {
  return (
    <>
      <SEO 
        title="About Us - Our Story & Mission"
        description="Fractallogy builds software that empowers businesses. We specialize in product engineering, innovation, and modern development using our Vibe Coding methodology."
        keywords="about Fractallogy, software development, product engineering, Vibe Coding, industry solutions, water monitoring, energy metering, optical CRM, FractoStudio, online video editor"
        url="/about"
      />

      {/* Hero Section */}
      <section className="about-hero-monks">
        <div className="container">
          <span className="section-label">About Us</span>
          <h1>
            We build software that <span className="circled">empowers</span> businesses to thrive.
          </h1>
        </div>
      </section>

      {/* Intro Section */}
      <section className="about-intro-monks">
        <div className="container">
          <div className="intro-content">
            <p className="lead-text">
              At Fractallogy, we are passionate builders of software that empowers businesses to thrive in the digital age. With strong expertise in product engineering, innovation, and modern development practices, we partner with ambitious companies to design, build, and scale transformational solutions.
            </p>
            <p>
              We believe that great software isn't just about code — it's about solving real business problems with elegant, intuitive, and reliable products. Our team combines deep technical knowledge with strategic thinking and a relentless focus on quality, performance, and user experience.
            </p>
          </div>
        </div>
      </section>

      {/* The Name Section */}
      <section className="about-name-monks">
        <div className="container">
          <span className="section-label">Our Name</span>
          <h2>
            Inspired by <span className="italic">fractal</span> mathematics.
          </h2>
          <div className="name-content">
            <p className="lead-text">
              Fractallogy is inspired by fractal mathematics, where complex systems grow from simple, repeating building blocks.
            </p>
            <div className="fractal-grid">
              <div className="fractal-card">
                <div className="fractal-icon">
                  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor">
                    <circle cx="32" cy="32" r="28"/>
                    <circle cx="32" cy="32" r="18"/>
                    <circle cx="32" cy="32" r="10"/>
                    <circle cx="32" cy="32" r="4"/>
                  </svg>
                </div>
                <h4>Self-similar</h4>
                <p>Patterns that repeat at every scale, creating consistency and harmony.</p>
              </div>
              <div className="fractal-card">
                <div className="fractal-icon">
                  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor">
                    <rect x="4" y="4" width="56" height="56" rx="2"/>
                    <rect x="14" y="14" width="36" height="36" rx="2"/>
                    <rect x="22" y="22" width="20" height="20" rx="2"/>
                    <rect x="28" y="28" width="8" height="8" rx="1"/>
                  </svg>
                </div>
                <h4>Recursive</h4>
                <p>Solutions that build upon themselves, growing stronger with each iteration.</p>
              </div>
              <div className="fractal-card">
                <div className="fractal-icon">
                  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor">
                    <path d="M8 56 L32 8 L56 56"/>
                    <path d="M16 44 L32 20 L48 44"/>
                    <path d="M24 32 L32 20"/>
                    <path d="M40 32 L32 20"/>
                    <circle cx="32" cy="8" r="3"/>
                    <circle cx="8" cy="56" r="3"/>
                    <circle cx="56" cy="56" r="3"/>
                  </svg>
                </div>
                <h4>Infinitely scalable</h4>
                <p>Architecture designed to grow without limits as your business expands.</p>
              </div>
              <div className="fractal-card">
                <div className="fractal-icon">
                  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor">
                    <circle cx="32" cy="32" r="6"/>
                    <path d="M32 4v16M32 44v16"/>
                    <path d="M4 32h16M44 32h16"/>
                    <path d="M12 12l12 12M40 40l12 12"/>
                    <path d="M52 12l-12 12M24 40l-12 12"/>
                  </svg>
                </div>
                <h4>Built from simple rules</h4>
                <p>Complex outcomes emerging from elegant, fundamental principles.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="about-who-monks">
        <div className="container">
          <span className="section-label">Who We Are</span>
          <h2>
            Founded to help companies move <span className="underlined">faster</span> and <span className="underlined">smarter</span>.
          </h2>
          <div className="who-content">
            <p>
              Our core strength lies in product development — understanding customer needs, designing thoughtful solutions, and delivering them rapidly with precision.
            </p>
            <div className="highlight-box">
              <p>
                We leverage a unique internal methodology called <strong>AI-Assisted Coding</strong>, which fuels faster development cycles without compromising on quality or scalability.
              </p>
            </div>
            <p>
              Our team includes software architects, full-stack developers, UX/UI designers, and product strategists who work collaboratively through every stage of development — from discovery and prototyping to deployment and iteration.
            </p>
          </div>
        </div>
      </section>

      {/* What We Build Section */}
      <section className="about-products-monks">
        <div className="container">
          <span className="section-label">What We Build</span>
          <h2>
            High-impact products that address <span className="circled">real</span> challenges.
          </h2>
          
          <div className="products-list">
            <Link to="/online-video-editor" className="product-item product-item--link product-item--video">
              <div className="product-item__thumb" aria-hidden="true">
                <img src="/images/fracto-video-logo.png" alt="" />
              </div>
              <div className="product-content">
                <h3>FractoStudio — Online Video Editor</h3>
                <p>
                  Edit podcasts, Shorts, Reels, and tutorials in your browser—multi-cam sync, captions, filters, and
                  timeline tools without installing desktop software.
                </p>
                <p>
                  Free forever with no watermark on exports. Your media stays local while you cut, resize, subtitle,
                  and publish in the formats your platforms expect.
                </p>
              </div>
            </Link>

            <Link to="/industry-solutions/water-monitoring" className="product-item product-item--link">
              <div className="product-item__thumb" aria-hidden="true">
                <img src="/images/menu_icons/water_monitoring.png" alt="" />
              </div>
              <div className="product-content">
                <h3>Water Monitoring</h3>
                <p>
                  Bring clarity to every drop: connected metering, remote reads, and live dashboards so utilities and
                  facilities can see consumption, pressure, and quality at a glance.
                </p>
                <p>
                  Spot leaks and anomalies early, tighten compliance reporting, and give operations teams the signal
                  they need—before issues ripple into outages, waste, or customer impact.
                </p>
              </div>
            </Link>

            <Link to="/industry-solutions/energy-metering" className="product-item product-item--link">
              <div className="product-item__thumb" aria-hidden="true">
                <img src="/images/menu_icons/energy_monitoring.png" alt="" />
              </div>
              <div className="product-content">
                <h3>Energy Metering</h3>
                <p>
                  Wireless metering and interval analytics make energy visible across buildings, plants, and distributed
                  sites—without the drag of manual reads or spreadsheet gymnastics.
                </p>
                <p>
                  Track demand, power quality, and alarms in one place so teams can reduce waste, improve reliability,
                  and make tariff and capacity decisions with confidence.
                </p>
              </div>
            </Link>

            <Link to="/industry-solutions/air-quality" className="product-item product-item--link">
              <div className="product-item__thumb" aria-hidden="true">
                <img src="/images/menu_icons/air_quality.png" alt="" />
              </div>
              <div className="product-content">
                <h3>Air Quality</h3>
                <p>
                  Build an environmental intelligence layer for campuses, cities, and industrial zones—continuous
                  sensing with thresholds that turn raw readings into clear, timely context.
                </p>
                <p>
                  When pollution spikes or conditions drift, alerts reach the right people fast—supporting healthier
                  spaces, cleaner operations, and better long-term planning.
                </p>
              </div>
            </Link>

            <Link to="/industry-solutions/steam-measurement" className="product-item product-item--link">
              <div className="product-item__thumb" aria-hidden="true">
                <img src="/images/menu_icons/steam_monitoring.png" alt="" />
              </div>
              <div className="product-content">
                <h3>Steam Measurement</h3>
                <p>
                  See steam like a critical utility: compensated flow, pressure, and temperature signals that reflect
                  what is really moving through your lines and equipment.
                </p>
                <p>
                  Pair metering with trap and efficiency insights to cut losses, reduce unplanned downtime, and align
                  maintenance with conditions—not calendars.
                </p>
              </div>
            </Link>

            <a href="https://chatbot.fractallogy.com/" target="_blank" rel="noopener noreferrer" className="product-item product-item--link">
              <div className="product-item__thumb product-item__thumb--gif" aria-hidden="true">
                <img src="https://chatbot.fractallogy.com/static/chatbot_images/chatbot.gif" alt="" />
              </div>
              <div className="product-content">
                <h3>Fracto Chatbot</h3>
                <p>
                  Give customers instant answers on your site—order status, FAQs, product guidance—while your team
                  stays focused on the conversations that truly need a human touch.
                </p>
                <p>
                  Launch quickly with a chatbot tuned to your business, capture leads around the clock, and iterate as
                  your content and workflows evolve.
                </p>
              </div>
            </a>

            <Link to="/optical-crm" className="product-item product-item--link">
              <div className="product-item__thumb" aria-hidden="true">
                <img src="/images/main-slider/pos_crm.png" alt="" />
              </div>
              <div className="product-content">
                <h3>Optical CRM</h3>
                <p>
                  Run eyewear retail with one connected workflow—consultations, prescriptions, orders, and stock—so
                  front-desk speed matches back-office accuracy.
                </p>
                <p>
                  From GST-ready billing and multi-store inventory to reminders and loyalty, teams get the tools to
                  serve patients better and grow repeat business without operational friction.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Vibe Coding Section */}
      <section className="about-vibe-monks">
        <div className="container">
          <span className="section-label">Our Philosophy</span>
          <h2>
            <span className="italic">AI-Assisted Coding</span> — blending speed with discipline.
          </h2>
          
          <div className="vibe-grid">
            <div className="vibe-card">
              <h4>Velocity with Value</h4>
              <p>Rapid development cycles built around meaningful outcomes, not rushed features.</p>
            </div>
            <div className="vibe-card">
              <h4>Iterative Excellence</h4>
              <p>Continuous delivery and frequent feedback loops to refine and improve the product.</p>
            </div>
            <div className="vibe-card">
              <h4>Clarity & Collaboration</h4>
              <p>Transparent communication and shared understanding across teams and stakeholders.</p>
            </div>
            <div className="vibe-card">
              <h4>Quality First</h4>
              <p>Automated testing, modular architecture, and performance optimization at every step.</p>
            </div>
          </div>

          <p className="vibe-tagline">
            This philosophy ensures that we not only ship fast, but <strong>ship right</strong>.
          </p>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="about-why-monks">
        <div className="container">
          <div className="why-layout">
            <div className="why-heading">
              <span className="section-label">Why Choose Us</span>
              <h2>
                You don't just get developers — you get <span className="circled">partners</span> in innovation.
              </h2>
            </div>
            <div className="why-list">
              <div className="why-item">
                <span className="arrow">→</span>
                <p>A product mindset with a focus on usability and business impact.</p>
              </div>
              <div className="why-item">
                <span className="arrow">→</span>
                <p>Modern technology stacks and scalable architectures.</p>
              </div>
              <div className="why-item">
                <span className="arrow">→</span>
                <p>Strong emphasis on testing, performance, and reliability.</p>
              </div>
              <div className="why-item">
                <span className="arrow">→</span>
                <p>Proven experience building complex systems and marketplaces.</p>
              </div>
              <div className="why-item">
                <span className="arrow">→</span>
                <p>A trusted process that keeps risk low and delivery predictable.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactHome />
      <Footer />
    </>
  );
};

export default AboutPage;
